import {
  ignoreSignalAbort,
  useErrorHandlers,
} from "@/errors/components/error-handling-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  RegistrationEdgeRevision,
  RegistrationRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useEffect, useState } from "react";
import { selectRevisionRegistrationEdges } from "../store/revision/revision-edge-selectors";
import {
  selectRegistrationRevision,
  selectRevisionEntities,
} from "../store/revision/revision-entity-selectors";
import { loadRevisionDataThunk } from "./load-revision-data-thunk";

type UseLoadRevisionDataResult = {
  /** The loaded revision */
  revision: RegistrationRevision | undefined;
  /** The loaded entities */
  entities: CaptureTreeEntityRevision[] | undefined;
  /** The loaded edges */
  edges: RegistrationEdgeRevision[] | undefined;
  /** Whether the revision or its entities are still loading. */
  isLoading: boolean;
};

/**
 * Load all revision data, on mount and when the revision ID changes.
 *
 * @param revisionId The ID of the revision to load. If not provided, loads the latest draft revision.
 * @returns An object containing the revision, entities, edges, and loading state.
 */
export function useLoadRevisionData(
  revisionId?: GUID,
): UseLoadRevisionDataResult {
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithPage } = useErrorHandlers();

  const [isLoading, setIsLoading] = useState(true);

  const revision = useAppSelector(selectRegistrationRevision);
  const entities = useAppSelector(selectRevisionEntities);
  const edges = useAppSelector(selectRevisionRegistrationEdges);

  // Load the data whenever the revision changes
  useEffect(() => {
    const abortController = new AbortController();

    async function loadData(): Promise<void> {
      const result = await dispatch(
        loadRevisionDataThunk({
          revisionId,
          projectApiClient,
          signal: abortController.signal,
        }),
      ).catch(ignoreSignalAbort(handleErrorWithPage));

      if (result) {
        // Never set to true again; only showing loading screen for initial load
        setIsLoading(false);
      }
    }

    loadData();

    return () => {
      abortController.abort();
    };
  }, [dispatch, handleErrorWithPage, projectApiClient, revisionId]);

  return {
    revision,
    entities,
    edges,
    isLoading,
  };
}
